/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, PureComponent } from 'react'
import cx from 'classnames'

import type { EuiTabbedContentTab } from '@elastic/eui'
import { EuiFlexGroup, EuiFlexItem, EuiSpacer, EuiText, EuiTitle } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { RoutableBreadcrumb } from '@modules/ui-types'

import OrganizationEmptyPrompt from '@/components/Organization/OrganizationEmptyPrompt'
import BillingOverview from '@/components/Billing/BillingOverview'
import BillingHistory from '@/components/Billing/BillingHistory'
import BillingUsage from '@/components/Billing/BillingUsage'
import {
  billingOverviewCrumbs,
  billingHistoryCrumbs,
  costAnalysisCrumbs,
  billingCrumbs,
  billingSubscriptionCrumbs,
} from '@/lib/crumbBuilder'
import AccessDeniedPanel from '@/components/ErrorPages/AccessDenied/AccessDeniedPanel'
import AppLoadingRoot from '@/components/AppLoadingRoot'

import PortalPage from '../PortalPage'

import type { ReactElement } from 'react'
import type { AllProps } from './types'

class Billing extends PureComponent<AllProps> {
  componentDidMount(): void {
    const { pathname } = this.props.router.location

    // remove trailing "/"
    if (pathname.slice(-1) === '/') {
      this.props.router.navigate(pathname.slice(0, -1), { replace: true })
    }
  }

  render(): ReactElement | null {
    const { isRouteFSTraced, organizationId, showOrganization, permissions } = this.props

    if (permissions.isLoading) {
      return <AppLoadingRoot />
    }

    if (showOrganization && !organizationId) {
      return (
        <PortalPage
          breadcrumbs={billingCrumbs()}
          className={cx({ 'fs-unmask': isRouteFSTraced }, 'cloud-portal-account')}
        >
          <OrganizationEmptyPrompt />
        </PortalPage>
      )
    }

    if (!permissions.hasBillingPermissions) {
      return <AccessDeniedPanel />
    }

    const tabbedContent = this.getTabbedContent()
    const breadcrumbs = this.getBreadcrumbs(tabbedContent.selectedTab)

    return (
      <PortalPage
        breadcrumbs={breadcrumbs}
        className={cx({ 'fs-unmask': isRouteFSTraced }, 'cloud-portal-account')}
        contentHeader={
          <EuiFlexGroup direction='column' gutterSize='s' responsive={false}>
            <EuiFlexItem>
              <EuiTitle size='l' data-test-id='billing-page'>
                <h2>
                  <FormattedMessage id='cloud.billing.title' defaultMessage='Billing' />
                </h2>
              </EuiTitle>
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiText size='s' color='subdued' data-test-id='billing-page.organization-id'>
                <FormattedMessage
                  id='cloud.billing.organization-id'
                  defaultMessage='Organization ID: {organizationId}'
                  values={{ organizationId }}
                />
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        }
        tabbedContent={tabbedContent}
      />
    )
  }

  renderTabContent(content: ReactElement): ReactElement {
    return (
      <Fragment>
        <EuiSpacer size='l' />
        {content}
      </Fragment>
    )
  }

  getTabbedContent(): { tabs: EuiTabbedContentTab[]; selectedTab: EuiTabbedContentTab } {
    const { tabId, profile } = this.props

    const tabs: EuiTabbedContentTab[] = [
      {
        id: 'overview',
        name: 'Overview',
        content: this.renderTabContent(
          <BillingOverview data-test-id='billing-index-overview-link' />,
        ),
      },
      {
        id: 'usage',
        name: 'Usage',
        content: this.renderTabContent(<BillingUsage />),
      },
      {
        id: 'history',
        name: 'History',
        content: this.renderTabContent(
          <BillingHistory data-test-id='billing-index-history-link' profile={profile} />,
        ),
      },
    ]

    const selectedTab = tabs.find(({ id }) => id === tabId) || tabs[0]!

    return { tabs, selectedTab }
  }

  getBreadcrumbs(initialSelectedTab: EuiTabbedContentTab): RoutableBreadcrumb[] {
    const crumbs = {
      overview: billingOverviewCrumbs,
      usage: costAnalysisCrumbs,
      history: billingHistoryCrumbs,
      subscription: billingSubscriptionCrumbs,
    }

    return [...billingCrumbs(), ...crumbs[initialSelectedTab.id]()]
  }
}

export default Billing
